/**
 * Tasks Service - manages tasks with Linear-style workflow
 *
 * Workflow states (Linear-inspired):
 * - triage: New issues to be triaged
 * - backlog: Accepted but not started
 * - in_progress: Currently being worked on
 * - in_review: Awaiting review/approval
 * - done: Completed
 * - cancelled: Cancelled/won't do
 */

import type { Task, TaskStatus, TaskPriority, LinkedEntity } from '../types';
import { TASK_PRIORITY_CONFIG } from '../types';
import {
  getStore,
  addEntity,
  getEntity,
  getEntitiesByWorkspace,
  updateEntity,
  deleteEntity,
} from '../store';
import { generateId } from '../id';

let taskCounter = 1;

export interface CreateTaskInput {
  workspaceId: string;
  title: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: Date;
  assigneeId?: string;
  reporterId?: string;
  tags?: string[];
  linkedEntities?: LinkedEntity[];
  estimate?: number;
  cycleId?: string;
  projectId?: string;
}

export function createTask(input: CreateTaskInput): Task {
  const now = new Date();
  const identifier = `TASK-${String(taskCounter++).padStart(3, '0')}`;

  const task: Task = {
    id: generateId('task'),
    workspaceId: input.workspaceId,
    title: input.title,
    description: input.description ?? '',
    status: input.status ?? 'triage',
    priority: input.priority ?? 'no_priority',
    dueDate: input.dueDate,
    assigneeId: input.assigneeId,
    reporterId: input.reporterId,
    tags: input.tags ?? [],
    linkedEntities: input.linkedEntities ?? [],
    identifier,
    estimate: input.estimate,
    cycleId: input.cycleId,
    projectId: input.projectId,
    createdAt: now,
    updatedAt: now,
  };

  return addEntity(getStore().tasks, task);
}

export function getTask(id: string): Task | undefined {
  return getEntity(getStore().tasks, id);
}

export function getTasksByWorkspace(workspaceId: string): Task[] {
  return getEntitiesByWorkspace(getStore().tasks, workspaceId);
}

export function getTasksByStatus(workspaceId: string, status: TaskStatus): Task[] {
  return getTasksByWorkspace(workspaceId).filter((task) => task.status === status);
}

export function getActiveTasks(workspaceId: string): Task[] {
  return getTasksByWorkspace(workspaceId).filter(
    (task) => task.status !== 'done' && task.status !== 'cancelled'
  );
}

export function getOverdueTasks(workspaceId: string): Task[] {
  const now = new Date();
  return getTasksByWorkspace(workspaceId).filter(
    (task) =>
      task.dueDate &&
      new Date(task.dueDate) < now &&
      task.status !== 'done' &&
      task.status !== 'cancelled'
  );
}

export function getTasksDueToday(workspaceId: string): Task[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return getTasksByWorkspace(workspaceId).filter(
    (task) =>
      task.dueDate &&
      new Date(task.dueDate) >= today &&
      new Date(task.dueDate) < tomorrow &&
      task.status !== 'done' &&
      task.status !== 'cancelled'
  );
}

export function getCompletedThisWeek(workspaceId: string): Task[] {
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);

  return getTasksByWorkspace(workspaceId).filter(
    (task) => task.status === 'done' && new Date(task.updatedAt) >= weekAgo
  );
}

export function updateTask(
  id: string,
  updates: Partial<Omit<Task, 'id' | 'workspaceId' | 'createdAt'>>
): Task | undefined {
  return updateEntity(getStore().tasks, id, updates);
}

export function updateTaskStatus(id: string, status: TaskStatus): Task | undefined {
  return updateTask(id, { status });
}

export function updateTaskPriority(id: string, priority: TaskPriority): Task | undefined {
  return updateTask(id, { priority });
}

export function assignTask(id: string, assigneeId: string | undefined): Task | undefined {
  return updateTask(id, { assigneeId });
}

export function deleteTask(id: string): boolean {
  return deleteEntity(getStore().tasks, id);
}

export function completeTask(id: string): Task | undefined {
  return updateTaskStatus(id, 'done');
}

// Move task to next status in workflow
export function moveTaskForward(id: string): Task | undefined {
  const task = getTask(id);
  if (!task) return undefined;

  const workflow: Record<TaskStatus, TaskStatus | null> = {
    triage: 'backlog',
    backlog: 'in_progress',
    in_progress: 'in_review',
    in_review: 'done',
    done: null,
    cancelled: null,
  };

  const nextStatus = workflow[task.status];
  if (nextStatus) {
    return updateTaskStatus(id, nextStatus);
  }
  return task;
}

// Move task to previous status in workflow
export function moveTaskBackward(id: string): Task | undefined {
  const task = getTask(id);
  if (!task) return undefined;

  const workflow: Record<TaskStatus, TaskStatus | null> = {
    triage: null,
    backlog: 'triage',
    in_progress: 'backlog',
    in_review: 'in_progress',
    done: 'in_review',
    cancelled: 'backlog',
  };

  const prevStatus = workflow[task.status];
  if (prevStatus) {
    return updateTaskStatus(id, prevStatus);
  }
  return task;
}

export function getPriorityTasks(workspaceId: string): Task[] {
  return getTasksByWorkspace(workspaceId)
    .filter((task) => task.status !== 'done' && task.status !== 'cancelled')
    .sort((a, b) => {
      const priorityA = TASK_PRIORITY_CONFIG[a.priority].level;
      const priorityB = TASK_PRIORITY_CONFIG[b.priority].level;
      return priorityB - priorityA;
    });
}

export function getTasksByAssignee(workspaceId: string, assigneeId: string): Task[] {
  return getTasksByWorkspace(workspaceId).filter((task) => task.assigneeId === assigneeId);
}

export function getTaskStats(workspaceId: string): {
  total: number;
  byStatus: Record<TaskStatus, number>;
  overdue: number;
  dueToday: number;
  completedThisWeek: number;
} {
  const tasks = getTasksByWorkspace(workspaceId);
  const statuses: TaskStatus[] = ['triage', 'backlog', 'in_progress', 'in_review', 'done', 'cancelled'];

  return {
    total: tasks.length,
    byStatus: statuses.reduce((acc, status) => {
      acc[status] = tasks.filter((t) => t.status === status).length;
      return acc;
    }, {} as Record<TaskStatus, number>),
    overdue: getOverdueTasks(workspaceId).length,
    dueToday: getTasksDueToday(workspaceId).length,
    completedThisWeek: getCompletedThisWeek(workspaceId).length,
  };
}
